// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, TextInput, useWindowDimensions, View } from 'react-native';
import { Redirect, useLocalSearchParams, usePathname, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { useUser } from './user-context';
import {
  getSharedBffWebSocket,
  PlayersSnapshotEvent,
  PlayerTemporaryCreatedEvent,
  TourCreatedEvent,
  TourInitiateRejectedEvent,
} from '@/services/bffWebSocket';
import { setCurrentRoundId } from '@/services/currentRound';
import { setCurrentTourId } from '@/services/currentTour';
import { useI18n } from '@/i18n';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import {
  areSameIdLists,
  buildTourInitiatePlayerIds,
  getCreatorPlayerId,
  normalizeTourCreateSelection,
  toggleTourMemberSelection,
} from '@/services/tourCreateSelection';
import TournamentsScreen from './tournaments.screen';
import type { TourRoundSummary } from '@/features/round-init/types';
import type { GameDefinition } from '@/features/tour-leaderboard/types';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { getAssetImageSource } from '@/services/assetImageSource';
import { tournamentsCreateCoachMarkKey, useCoachMark } from '@/services/coachMarks';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';

const MOBILE_BREAKPOINT = 820;
const MAX_PLAYERS = 8;
let createTourCoachMarkDismissedForSession = false;

type TourSummary = {
  id: string;
  tourName?: string;
  status?: string;
  isHidden?: boolean;
  playerNames?: string[];
  createdAt?: string;
  imageKey?: string | null;
  imageThumbKey?: string | null;
};

type CandidatePlayer = {
  id: string;
  name: string;
  userId?: string | null;
  isTemporary?: boolean;
};

type TourRoundsState = {
  loading: boolean;
  error: string | null;
  rounds: TourRoundSummary[];
};

type RoundGamesState = {
  loading: boolean;
  error: string | null;
  gameIds: string[];
};

export default function TournamentsWebScreen() {
  const { width } = useWindowDimensions();
  const isMobileLayout = width < MOBILE_BREAKPOINT;

  const { debug: debugParam, startTour: startTourParam } = useLocalSearchParams<{ debug?: string; startTour?: string }>();
  const pathname = usePathname();
  const isMyToursPage = pathname === '/my-tours' || pathname === '/my-tours/';
  const isCreateTourPage = pathname === '/tournaments' || pathname === '/tournaments/';
  const shouldAutoStartTour = startTourParam === 'true' || startTourParam === '1';
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user, clear } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const trophyImage = useMemo(() => getAssetImageSource('images/trophy.png', 'icon', '/tournaments'), []);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const createTourButtonAnchorRef = useRef<View>(null);
  const [createTourCoachMarkDismissed, setCreateTourCoachMarkDismissed] = useState(
    createTourCoachMarkDismissedForSession
  );
  const {
    visible: showCreateTourCoachMark,
    shownCount: createTourCoachMarkShownCount,
    maxShows: createTourCoachMarkMaxShows,
  } = useCoachMark(
    apiBaseUrl,
    tournamentsCreateCoachMarkKey,
    !createTourCoachMarkDismissed && isFocused
  );
  const dismissCreateTourCoachMark = useCallback(() => {
    createTourCoachMarkDismissedForSession = true;
    setCreateTourCoachMarkDismissed(true);
  }, []);
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [statusNote, setStatusNote] = useState<string>(t('tournaments.loading'));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [tourStarting, setTourStarting] = useState(false);
  const [newTourName, setNewTourName] = useState(() => t('tournaments.defaultName'));
  const [openTourMenuId, setOpenTourMenuId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState<CandidatePlayer[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [temporaryPlayerName, setTemporaryPlayerName] = useState('');
  const [loadingPlatformPlayers, setLoadingPlatformPlayers] = useState(false);
  const [isEnsuringPlayer, setIsEnsuringPlayer] = useState(false);
  const [ensurePlayerError, setEnsurePlayerError] = useState<string | null>(null);
  const [ensureStartedAt, setEnsureStartedAt] = useState<number | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTourName, setShareModalTourName] = useState<string | null>(null);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const pendingTemporaryCreateIdsRef = useRef(new Set<string>());
  const [pendingTemporaryCreates, setPendingTemporaryCreates] = useState(0);
  const [roundsByTourId, setRoundsByTourId] = useState<Record<string, TourRoundsState>>({});
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [roundGamesByRoundId, setRoundGamesByRoundId] = useState<Record<string, RoundGamesState>>({});
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const pendingStatusRequestIdRef = useRef<string | null>(null);
  const pendingTourPayloadRef = useRef<Record<string, unknown> | null>(null);
  const autoStartTourTriggeredRef = useRef(false);
  const currentPlayerIdRef = useRef<string | null>(null);
  const ensureInFlightRef = useRef<Promise<boolean> | null>(null);
  const lastSnapshotRefreshAtRef = useRef(0);
  const initialEnsureStartedForUserIdRef = useRef<string | null>(null);
  const gameDefinitionsLoadedForUserIdRef = useRef<string | null>(null);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tournaments',
      }),
    [apiBaseUrl, user?.token]
  );

  const visibleTours = useMemo(() => {
    if (!isMyToursPage) return [];
    const nonHiddenTours = tours.filter((tour) => !tour.isHidden);
    if (showArchived) return nonHiddenTours;
    return nonHiddenTours.filter((tour) => (tour.status ?? '').toLowerCase() !== 'archived');
  }, [isMyToursPage, showArchived, tours]);
  const myToursAndRoundsSummary = useMemo(() => {
    if (!isMyToursPage) return null;
    const tourCount = visibleTours.length;
    const unfinishedRoundCount = visibleTours.reduce((total, tour) => {
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      const unfinishedRounds = rounds.filter((round) => {
        const status = (round.status ?? '').toLowerCase();
        return status !== 'cancelled' && status !== 'finished';
      });
      return total + unfinishedRounds.length;
    }, 0);
    if (tourCount === 0 && unfinishedRoundCount === 0) return null;
    return t('tournaments.myToursAndRoundsSummary', { tours: tourCount, rounds: unfinishedRoundCount });
  }, [isMyToursPage, roundsByTourId, t, visibleTours]);
  const unfinishedRoundRows = useMemo(() => {
    if (!isMyToursPage) return [] as Array<{ tourId: string; tourName: string; round: TourRoundSummary }>;
    const rows: Array<{ tourId: string; tourName: string; round: TourRoundSummary }> = [];
    tours.forEach((tour) => {
      if (!tour.isHidden) {
        return;
      }
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      rounds.forEach((round) => {
        const status = (round.status ?? '').toLowerCase();
        if (status === 'finished' || status === 'cancelled') {
          return;
        }
        rows.push({
          tourId: tour.id,
          tourName: tour.tourName ?? t('tournaments.defaultName'),
          round,
        });
      });
    });
    return rows.sort((a, b) => {
      const aTime = Date.parse(a.round.statusUpdatedAt ?? a.round.createdAt ?? '') || 0;
      const bTime = Date.parse(b.round.statusUpdatedAt ?? b.round.createdAt ?? '') || 0;
      return bTime - aTime;
    });
  }, [isMyToursPage, roundsByTourId, t, tours, visibleTours]);
  const creatorPlayerId = useMemo(
    () => getCreatorPlayerId(availablePlayers, user?.id, currentPlayerIdRef.current),
    [availablePlayers, user?.id]
  );
  const effectiveSelectedPlayerIds = useMemo(
    () => buildTourInitiatePlayerIds(selectedPlayerIds, creatorPlayerId, MAX_PLAYERS),
    [creatorPlayerId, selectedPlayerIds]
  );

  useEffect(() => {
    if (createTourCoachMarkShownCount === 0) {
      setCreateTourCoachMarkDismissed(false);
    }
  }, [createTourCoachMarkShownCount]);

  useEffect(() => {
    if (!isFocused) {
      setCreateTourCoachMarkDismissed(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (isFocused && createTourCoachMarkShownCount < createTourCoachMarkMaxShows) {
      setCreateTourCoachMarkDismissed(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (pathname === '/tournaments' && createTourCoachMarkShownCount < createTourCoachMarkMaxShows) {
      setCreateTourCoachMarkDismissed(false);
    }
  }, [createTourCoachMarkMaxShows, createTourCoachMarkShownCount, pathname]);

  const formatRoundStatus = useCallback(
    (round: TourRoundSummary) => {
      if (!round.status) return null;
      const normalized = round.status.toLowerCase();
      if (normalized === 'active' || normalized === 'finished') {
        const timestamp = round.statusUpdatedAt ?? round.createdAt;
        if (!timestamp) {
          return t('tournaments.roundStatus', { status: round.status });
        }
        const dateLabel = new Date(timestamp).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        return t('tournaments.roundStatusWithDate', { status: round.status, date: dateLabel });
      }
      return t('tournaments.roundStatus', { status: round.status });
    },
    [t]
  );

  const fetchRoundsForTour = useCallback(
    async (tourId: string) => {
      if (!user?.token) return;
      setRoundsByTourId((prev) => ({
        ...prev,
        [tourId]: {
          loading: true,
          error: null,
          rounds: prev[tourId]?.rounds ?? [],
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,open,active,playing,started,finished`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load rounds (${response.status})`);
        }
        const data = (await response.json()) as TourRoundSummary[];
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: null,
            rounds: data,
          },
        }));
      } catch (err) {
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: t('tournaments.roundsLoadError'),
            rounds: [],
          },
        }));
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const gameNameById = useMemo(
    () => new Map(gameDefinitions.map((game) => [game.id, getGameDisplayName(game, t('tournaments.gameFallbackName'))])),
    [gameDefinitions, t]
  );

  const fetchGameDefinitions = useCallback(async () => {
    if (!user?.token) return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load game definitions (${response.status})`);
      }
      const data = (await response.json()) as GameDefinition[];
      setGameDefinitions(data ?? []);
    } catch {
      setGameDefinitions([]);
    }
  }, [apiBaseUrl, user?.token]);

  const fetchRoundGames = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: {
          loading: true,
          error: null,
          gameIds: prev[roundId]?.gameIds ?? [],
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load round (${response.status})`);
        }
        const data = (await response.json()) as {
          gameDefinitions?: string[];
          GameDefinitions?: string[];
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        };
        const unique = Array.from(
          new Set(
            [
              ...(data.gameDefinitions ?? []),
              ...(data.GameDefinitions ?? []),
              ...(data.gameDefinitionIds ?? []),
              ...(data.GameDefinitionIds ?? []),
            ].filter(Boolean)
          )
        );
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: null,
            gameIds: unique,
          },
        }));
      } catch (err) {
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: t('tournaments.gamesLoadError'),
            gameIds: [],
          },
        }));
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const fetchTours = useCallback(async (playerIdOverride?: string) => {
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id && !playerIdOverride) return;
    setLoading(true);
    setError(null);
    try {
      let playerId = playerIdOverride ?? currentPlayerIdRef.current;
      if (!playerId && user?.id) {
        const playerResp = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (playerResp.status === 401 || playerResp.status === 403) {
          clear();
          router.replace('/');
          return;
        }
        if (playerResp.status === 404) {
          setTours([]);
          setStatusNote(t('tournaments.playerProvisioning'));
          return;
        }
        if (!playerResp.ok) throw new Error(await playerResp.text());
        const player = await (playerResp.json() as Promise<{ id?: string }>);
        playerId = player.id ?? null;
      }
      if (!playerId) {
        setTours([]);
        setStatusNote(t('tournaments.playerProvisioning'));
        return;
      }

      currentPlayerIdRef.current = playerId;
      const includeHiddenParam = isMyToursPage ? '&includeHidden=true' : '';
      const response = await fetch(`${apiBaseUrl}/api/tours?playerId=${playerId}${includeHiddenParam}`, {
        headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as Array<{
        id: string;
        tourName?: string;
        TourName?: string;
        status?: string;
        Status?: string;
        isHidden?: boolean;
        IsHidden?: boolean;
        playerNames?: string[];
        PlayerNames?: string[];
        createdAt?: string;
        CreatedAt?: string;
        imageKey?: string | null;
        ImageKey?: string | null;
        imageThumbKey?: string | null;
        ImageThumbKey?: string | null;
      }>;
      setTours(
        data.map((item) => ({
          id: item.id,
          tourName: item.tourName ?? item.TourName,
          status: item.status ?? item.Status,
          isHidden: item.isHidden ?? item.IsHidden ?? false,
          playerNames: item.playerNames ?? item.PlayerNames,
          createdAt: item.createdAt ?? item.CreatedAt,
          imageKey: item.imageKey ?? item.ImageKey ?? null,
          imageThumbKey: item.imageThumbKey ?? item.ImageThumbKey ?? null,
        }))
      );
      setStatusNote(data.length ? '' : t('tournaments.empty'));
      if (data.length === 0) {
        setCurrentTourId(null);
      }
    } catch {
      setError(t('tournaments.loadError'));
      setTours([]);
      setStatusNote('');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, isMyToursPage, user?.id, user?.token, clear, router, t]);
  const fetchPlatformPlayers = useCallback(async () => {
    if (!user?.token) return;
    setLoadingPlatformPlayers(true);
    try {
      type RawPlayer = {
        id?: string;
        Id?: string;
        name?: string;
        Name?: string;
        userId?: string | null;
        UserId?: string | null;
        isTemporary?: boolean;
        IsTemporary?: boolean;
      };
      const response = await fetch(`${apiBaseUrl}/api/players/platform`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      let payload: RawPlayer[];
      if (response.ok) {
        payload = (await response.json()) as RawPlayer[];
      } else if (response.status === 404 || response.status === 405) {
        const fallbackResponse = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!fallbackResponse.ok) {
          throw new Error(`Failed to load platform players (${response.status}/${fallbackResponse.status})`);
        }
        const fallbackPayload = (await fallbackResponse.json()) as
          | RawPlayer[]
          | {
              players?: RawPlayer[];
              Players?: RawPlayer[];
            };
        const allPlayers = Array.isArray(fallbackPayload)
          ? fallbackPayload
          : (fallbackPayload.players ?? fallbackPayload.Players ?? []);
        payload = allPlayers.filter((player) => (player.userId ?? player.UserId ?? null) !== null);
      } else {
        throw new Error(`Failed to load platform players (${response.status})`);
      }

      const normalized = payload
        .map((player) => ({
          id: player.id ?? player.Id ?? '',
          name: (player.name ?? player.Name ?? '').trim(),
          userId: player.userId ?? player.UserId ?? null,
          isTemporary: Boolean(player.isTemporary ?? player.IsTemporary),
        }))
        .filter((player) => player.id && player.name && player.userId)
        .sort((left, right) => left.name.localeCompare(right.name));
      setAvailablePlayers(normalized);
      setSelectedPlayerIds((prev) => {
        const normalizedCreatorPlayerId = getCreatorPlayerId(normalized, user?.id, currentPlayerIdRef.current);
        const next = normalizeTourCreateSelection(prev, normalized, normalizedCreatorPlayerId, MAX_PLAYERS);
        return areSameIdLists(prev, next) ? prev : next;
      });
    } catch (err) {
      setError(t('tournaments.membersLoadError'));
      logToBff('tournaments.members.load.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setLoadingPlatformPlayers(false);
    }
  }, [apiBaseUrl, logToBff, t, user?.id, user?.token]);

  const runEnsurePlayerFlow = useCallback(
    (options?: { force?: boolean; signal?: AbortSignal }) => {
      if (!user?.id || !user?.token) {
        return Promise.resolve(false);
      }
      if (ensureInFlightRef.current) {
        return ensureInFlightRef.current;
      }

      const promise = (async () => {
        const startedAt = Date.now();
        setIsEnsuringPlayer(true);
        setEnsureStartedAt(startedAt);
        if (options?.force) {
          setEnsurePlayerError(null);
        }
        setStatusNote(t('tournaments.playerProvisioning'));

        try {
          const result = await ensureCurrentPlayer(apiBaseUrl, user.token, user.id, {
            timeoutMs: 10_000,
            intervalMs: 400,
            signal: options?.signal,
          });

          if (result.status === 'resolved') {
            currentPlayerIdRef.current = result.playerId;
            setEnsurePlayerError(null);
            await fetchTours(result.playerId);
            await fetchPlatformPlayers();
            return true;
          }

          if (result.status === 'unauthorized') {
            clear();
            router.replace('/');
            return false;
          }

          if (result.status === 'timeout') {
            setEnsurePlayerError(t('tournaments.playerProvisioningTimeout'));
            setStatusNote(t('tournaments.playerProvisioningRetry'));
            logToBff('tournaments.player.ensure.timeout', { userId: user.id, startedAt }, 'warn');
            return false;
          }

          setEnsurePlayerError(t('tournaments.playerProvisioningRetry'));
          setStatusNote(t('tournaments.playerProvisioningRetry'));
          logToBff(
            'tournaments.player.ensure.error',
            { userId: user.id, startedAt, message: result.message },
            'warn'
          );
          return false;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return false;
          }

          setEnsurePlayerError(t('tournaments.playerProvisioningRetry'));
          setStatusNote(t('tournaments.playerProvisioningRetry'));
          logToBff(
            'tournaments.player.ensure.exception',
            { userId: user.id, startedAt, message: error instanceof Error ? error.message : String(error) },
            'warn'
          );
          return false;
        } finally {
          setIsEnsuringPlayer(false);
        }
      })();

      ensureInFlightRef.current = promise;
      return promise.finally(() => {
        if (ensureInFlightRef.current === promise) {
          ensureInFlightRef.current = null;
        }
      });
    },
    [apiBaseUrl, clear, fetchPlatformPlayers, fetchTours, logToBff, router, t, user?.id, user?.token]
  );

  useEffect(() => {
    if (!creatorPlayerId) {
      return;
    }

    setSelectedPlayerIds((prev) => {
      const next = buildTourInitiatePlayerIds(prev, creatorPlayerId, MAX_PLAYERS);
      return areSameIdLists(prev, next) ? prev : next;
    });
  }, [creatorPlayerId]);

  useEffect(() => {
    if (isMobileLayout) {
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      if ((message as { type?: string }).type === 'players_snapshot') {
        if (!isEnsuringPlayer && !ensurePlayerError && !!currentPlayerIdRef.current) {
          return;
        }

        const payload = message as PlayersSnapshotEvent;
        const players = payload.players ?? payload.Players ?? [];
        const hasCurrentUserPlayer = Boolean(user?.id) && players.some((player) => player.userId === user?.id);
        if (!hasCurrentUserPlayer) {
          return;
        }

        const now = Date.now();
        if (now - lastSnapshotRefreshAtRef.current < 500) {
          return;
        }
        lastSnapshotRefreshAtRef.current = now;

        void runEnsurePlayerFlow();
        return;
      }

      if ((message as { type?: string }).type === 'tour.initiate.rejected') {
        const payload = message as TourInitiateRejectedEvent;
        if (!pendingRequestIdRef.current || payload.requestId !== pendingRequestIdRef.current) {
          return;
        }
        pendingRequestIdRef.current = null;
        setTourStarting(false);
        const translated = payload.messageKey ? t(payload.messageKey) : '';
        const localized =
          translated && payload.messageKey && translated !== payload.messageKey
            ? translated
            : payload.message || t('tournaments.createFailed');
        setError(localized);
        setStatusNote(localized);
        return;
      }
      if ((message as { type?: string }).type === 'tour.update.ack') {
        pendingStatusRequestIdRef.current = null;
        setStatusNote(t('tournaments.statusUpdateAck'));
        return;
      }
      if ((message as { type?: string }).type === 'player.temporary.created') {
        const payload = message as PlayerTemporaryCreatedEvent;
        if (!payload.requestId || !pendingTemporaryCreateIdsRef.current.has(payload.requestId)) {
          return;
        }
        pendingTemporaryCreateIdsRef.current.delete(payload.requestId);
        setPendingTemporaryCreates(pendingTemporaryCreateIdsRef.current.size);
        const createdPlayerId = payload.playerId;
        const createdPlayerName = payload.name;
        if (!createdPlayerId || !createdPlayerName) {
          return;
        }
        setAvailablePlayers((prev) => {
          if (prev.some((player) => player.id === createdPlayerId)) {
            return prev;
          }
          return [
            ...prev,
            {
              id: createdPlayerId,
              name: createdPlayerName,
              userId: null,
              isTemporary: true,
            },
          ].sort((left, right) => left.name.localeCompare(right.name));
        });
        setSelectedPlayerIds((prev) => {
          if (prev.includes(createdPlayerId)) {
            return prev;
          }
          if (prev.length >= MAX_PLAYERS) {
            return prev;
          }
          return [...prev, createdPlayerId];
        });
        return;
      }
      const payload = message as TourCreatedEvent;
      if (payload.type === 'tour.created') {
        const incomingRequestId = payload.requestId ?? payload.RequestId;
        const isMatch = pendingRequestIdRef.current && incomingRequestId === pendingRequestIdRef.current;
        if (!isMatch) {
          fetchTours();
          setStatusNote(t('tournaments.createdNotice'));
          return;
        }
        pendingRequestIdRef.current = null;
        setTourStarting(false);
        const createdId = payload.tourId ?? payload.TourId ?? null;
        setCurrentTourId(createdId);
        setStatusNote(t('tournaments.createdWithId', { id: createdId }));
        fetchTours();
        const debugSuffix = debug ? '?debug=true' : '';
        if (createdId) {
          router.push(`/tour/${createdId}/edit${debugSuffix}`);
        }
      }
    });
    const unsubscribeError = socket.onError(() => null);
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state !== 'connected') return;
      if (!pendingTourPayloadRef.current) return;
      const payload = pendingTourPayloadRef.current;
      pendingTourPayloadRef.current = null;
      const sent = socket.send(payload);
      if (sent) {
        setStatusNote(t('tournaments.creating'));
      } else {
        pendingTourPayloadRef.current = payload;
      }
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      unsubscribeConnection();
    };
  }, [debug, ensurePlayerError, fetchTours, isEnsuringPlayer, isFocused, isMobileLayout, router, runEnsurePlayerFlow, user?.id, user?.token, t]);

  useEffect(() => {
    if (!tourStarting || !pendingRequestIdRef.current) return;
    const timer = setTimeout(() => {
      if (!pendingRequestIdRef.current) return;
      pendingRequestIdRef.current = null;
      setTourStarting(false);
      setStatusNote(t('tournaments.createFailed'));
    }, 12000);
    return () => clearTimeout(timer);
  }, [tourStarting, t]);

  const playerProvisioningBlocked = isEnsuringPlayer || !!ensurePlayerError;

  useEffect(() => {
    if (user?.id) return;
    initialEnsureStartedForUserIdRef.current = null;
    gameDefinitionsLoadedForUserIdRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady) return;
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id) return;
    if (initialEnsureStartedForUserIdRef.current === user.id) return;
    initialEnsureStartedForUserIdRef.current = user.id;

    const abortController = new AbortController();
    void runEnsurePlayerFlow({ force: true, signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, [clear, isMobileLayout, isNavigationReady, router, runEnsurePlayerFlow, user?.id, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady || !user?.id || !user?.token) return;
    if (gameDefinitionsLoadedForUserIdRef.current === user.id) return;
    gameDefinitionsLoadedForUserIdRef.current = user.id;
    void fetchGameDefinitions();
  }, [fetchGameDefinitions, isMobileLayout, isNavigationReady, user?.id, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!user?.token || !isNavigationReady) return;
    tours.forEach((tour) => {
      fetchRoundsForTour(tour.id);
    });
  }, [fetchRoundsForTour, isMobileLayout, isNavigationReady, tours, user?.token]);

  useEffect(() => {
    if (isMobileLayout) return;
    if (!isNavigationReady || !user?.token) return;
    Object.values(roundsByTourId).forEach((state) => {
      state.rounds.forEach((round) => {
        if (!roundGamesByRoundId[round.id]) {
          fetchRoundGames(round.id);
        }
      });
    });
  }, [fetchRoundGames, isMobileLayout, isNavigationReady, roundGamesByRoundId, roundsByTourId, user?.token]);

  const togglePlayerSelection = (playerId: string) => {
    if (playerProvisioningBlocked) {
      setError(t('tournaments.playerProvisioningBlocked'));
      return;
    }
    setError(null);
    setSelectedPlayerIds((prev) => {
      const result = toggleTourMemberSelection(prev, playerId, creatorPlayerId, MAX_PLAYERS);
      if (result.blockedByCreator) {
        setError(t('tournaments.creatorMemberLocked'));
        return prev;
      }
      if (result.blockedByMax) {
        setStatusNote(t('home.maxPlayers'));
        return prev;
      }
      return result.nextSelectedPlayerIds;
    });
  };

  const handleCreateTemporaryPlayer = () => {
    if (playerProvisioningBlocked) {
      setError(t('tournaments.playerProvisioningBlocked'));
      return;
    }
    const name = temporaryPlayerName.trim();
    if (!name) {
      setError(t('players.temporaryNameRequired'));
      return;
    }
    if (effectiveSelectedPlayerIds.length >= MAX_PLAYERS) {
      setError(t('home.maxPlayers'));
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    const sent = socket.send({
      type: 'player.temporary.create',
      requestId,
      name,
    });
    if (!sent) {
      setError(t('players.temporaryCreateFailed'));
      return;
    }

    pendingTemporaryCreateIdsRef.current.add(requestId);
    setPendingTemporaryCreates(pendingTemporaryCreateIdsRef.current.size);
    setTemporaryPlayerName('');
    setError(null);
  };

  const handleRetryProvisioning = () => {
    setEnsurePlayerError(null);
    void runEnsurePlayerFlow({ force: true });
  };

  const handleNewTour = () => {
    if (tourStarting) return;
    if (playerProvisioningBlocked) {
      setError(t('tournaments.playerProvisioningBlocked'));
      return;
    }
    if (pendingTemporaryCreateIdsRef.current.size > 0) {
      setError(t('players.temporaryCreatePending'));
      return;
    }
    if (!creatorPlayerId) {
      setError(t('tournaments.creatorMemberRequired'));
      return;
    }

    const playerIds = buildTourInitiatePlayerIds(selectedPlayerIds, creatorPlayerId, MAX_PLAYERS);
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    pendingRequestIdRef.current = requestId;
    setTourStarting(true);
    setError(null);
    setStatusNote(t('tournaments.creating'));
    const payload = {
      type: 'tour.initiate',
      requestId,
      tourName: newTourName.trim() || t('tournaments.defaultName'),
      playerIds,
    };
    const sent = socket.send(payload);
    if (!sent) {
      pendingTourPayloadRef.current = payload;
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
  };

  useEffect(() => {
    if (!shouldAutoStartTour) {
      autoStartTourTriggeredRef.current = false;
      return;
    }
    if (!isCreateTourPage || isMobileLayout) return;
    if (!isNavigationReady || !user?.token) return;
    if (tourStarting || playerProvisioningBlocked) return;
    if (pendingTemporaryCreateIdsRef.current.size > 0) return;
    if (!creatorPlayerId) return;
    if (autoStartTourTriggeredRef.current) return;
    autoStartTourTriggeredRef.current = true;
    handleNewTour();
  }, [
    creatorPlayerId,
    handleNewTour,
    isCreateTourPage,
    isMobileLayout,
    isNavigationReady,
    playerProvisioningBlocked,
    shouldAutoStartTour,
    tourStarting,
    user?.token,
  ]);

  const updateTourStatus = (tourId: string, status: string) => {
    if (!user?.token) return;
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (!['archived', 'sleep', 'cancelled'].includes(status)) return;
    const requestId = generateId();
    pendingStatusRequestIdRef.current = requestId;
    setStatusNote(t('tournaments.statusUpdateSent', { status }));
    const payload = {
      type: 'tour.update',
      tourId,
      status,
      requestId,
    };
    const sent = socket.send(payload);
    if (!sent) {
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
    logToBff('tournaments.status.selected', { tourId, status, requestId });
    if (status === 'archived') {
      console.info('[tournaments] archive', { tourId, status });
    }
    setOpenTourMenuId(null);
    setTours((prev) => {
      if (status === 'archived' || status === 'cancelled') {
        return prev.filter((t) => t.id !== tourId);
      }
      return prev.map((t) => (t.id === tourId ? { ...t, status } : t));
    });
  };

  const resolveInviteLink = async (tourId: string) => {
    return resolveTourInviteLink({
      apiBaseUrl,
      tourId,
      authToken: user?.token,
    });
  };

  const handleOpenShareModal = async (tour: TourSummary) => {
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalTourName(tour.tourName ?? t('tournaments.defaultName'));
    setShareModalLink(null);
    setOpenTourMenuId(null);

    try {
      const inviteLink = await resolveInviteLink(tour.id);
      setShareModalLink(inviteLink);
      setShareModalOpen(true);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      setStatusNote(t('tournaments.shareLoadError'));
      logToBff('tournaments.share.link.error', {
        tourId: tour.id,
        message: shareError instanceof Error ? shareError.message : String(shareError),
      }, 'warn');
    } finally {
      setShareModalLoading(false);
    }
  };

  const handleShareInviteLink = async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          setStatusNote(t('tournaments.shareCopied'));
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  };

  if (isMobileLayout) {
    return <TournamentsScreen />;
  }

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const openRoundFromTour = (tourId: string, round: TourRoundSummary) => {
    setCurrentTourId(tourId);
    setCurrentRoundId(round.id);
    const debugSuffix = debug ? '?debug=true' : '';
    const normalizedRoundStatus = (round.status ?? '').toLowerCase();
    const roundPath = normalizedRoundStatus === 'finished'
      ? `/round/${round.id}/summary${debugSuffix}`
      : normalizedRoundStatus === 'draft'
        ? `/round/${round.id}/roundstart${debugSuffix}`
        : `/round/${round.id}/hole/1/compact${debugSuffix}`;
    router.push(roundPath);
  };

  return (
    <View style={styles.shell}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>{isMyToursPage ? t('welcome.myTours') : t('welcome.startTour')}</Text>
          <Text style={styles.meta}>{statusNote}</Text>
          {isMyToursPage && myToursAndRoundsSummary ? <Text style={styles.meta}>{myToursAndRoundsSummary}</Text> : null}
          {isMyToursPage && unfinishedRoundRows.length > 0 ? (
            <View style={styles.roundsBlock}>
              <Text style={styles.roundsTitle}>
                {t('tournaments.hiddenToursUnfinishedRoundsTitle')} ({unfinishedRoundRows.length})
              </Text>
              <View style={styles.roundsGrid}>
                {unfinishedRoundRows.map((row) => {
                  const roundName = row.round.name ?? row.round.courseName ?? t('tournaments.roundFallbackName');
                  const roundStatusLabel = formatRoundStatus(row.round);
                  const normalizedRoundStatus = (row.round.status ?? '').toLowerCase();
                  const isFinished = normalizedRoundStatus === 'finished';
                  const isDraft = normalizedRoundStatus === 'draft';
                  const roundActionLabel = isFinished
                    ? t('tournaments.roundResults')
                    : isDraft
                      ? t('tournaments.roundStart')
                      : t('tournaments.roundOpen');
                  return (
                    <View key={`${row.tourId}:${row.round.id}`} style={styles.roundRow}>
                      <View style={styles.roundCard}>
                        <View style={styles.roundHeaderRow}>
                          <View style={styles.roundHeader}>
                            <Text style={styles.roundName}>{roundName}</Text>
                            <Text style={styles.meta}>{row.tourName}</Text>
                            {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                          </View>
                          <View style={styles.hiddenRoundActions}>
                            <GoffyButton
                              label={roundActionLabel}
                              onPress={() => openRoundFromTour(row.tourId, row.round)}
                              size="compact"
                              tone="dark"
                              oldButtonStyle={styles.roundActionButton}
                              oldDisabledStyle={styles.primaryButtonDisabled}
                              style={styles.hiddenRoundActionButtonWrap}
                              textStyle={styles.roundActionButtonText}
                            />
                            <GoffyButton
                              label={t('tournaments.statusCancel')}
                              onPress={() => updateTourStatus(row.tourId, 'cancelled')}
                              size="compact"
                              tone="dark"
                              oldButtonStyle={styles.hiddenRoundCancelButton}
                              oldDisabledStyle={styles.primaryButtonDisabled}
                              style={styles.hiddenRoundActionButtonWrap}
                              textStyle={styles.hiddenRoundCancelButtonText}
                            />
                          </View>
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          ) : null}
        </View>
        {isMyToursPage ? (
          <Pressable
            style={[styles.toggleButton, showArchived && styles.toggleButtonActive]}
            onPress={() => setShowArchived((prev) => !prev)}
            accessibilityRole="switch"
            accessibilityState={{ checked: showArchived }}
          >
            <View style={styles.toggleIndicator}>
              <View style={[styles.toggleDot, showArchived && styles.toggleDotActive]} />
            </View>
            <Text style={styles.toggleButtonText}>
              {showArchived ? t('tournaments.hideArchived') : t('tournaments.showArchived')}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {isEnsuringPlayer ? (
        <View style={styles.provisioningRow}>
          <ActivityIndicator size="small" color={appColors.accent.primary} />
          <Text style={styles.meta}>{t('tournaments.playerProvisioning')}</Text>
          {ensureStartedAt ? <Text style={styles.meta}>{new Date(ensureStartedAt).toLocaleTimeString()}</Text> : null}
        </View>
      ) : null}
      {ensurePlayerError ? (
        <View style={styles.provisioningRetryRow}>
          <Text style={styles.error}>{ensurePlayerError}</Text>
          <Pressable style={styles.retryButton} onPress={handleRetryProvisioning}>
            <Text style={styles.retryButtonText}>{t('tournaments.playerProvisioningActionRetry')}</Text>
          </Pressable>
        </View>
      ) : null}
      <View style={styles.body}>
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {debug && user ? (
            <Text style={styles.debugUser}>User: {user.forename ?? user.id} ({user.id})</Text>
          ) : null}
          {loading ? <Text style={styles.meta}>{t('tournaments.loading')}</Text> : null}
          {isMyToursPage ? visibleTours.map((tour) => {
            const normalizedStatus = (tour.status ?? '').toLowerCase();
            const statusLabel = normalizedStatus === 'active' ? null : tour.status;
            const tourTitle = tour.tourName ?? 'Tour';
          const playerLine = (tour.playerNames ?? []).map((name) => name.trim()).filter(Boolean).join(', ');
          const rawImage = tour.imageThumbKey ?? tour.imageKey ?? '';
          const imageUrl = rawImage && rawImage.startsWith('http') ? rawImage : rawImage ? `${apiBaseUrl}/api/tours/${tour.id}/image` : '';
          const imageSource = imageUrl ? { uri: imageUrl } : trophyImage;
          const menuOpen = openTourMenuId === tour.id;
            const handleNewRound = () => {
              setCurrentTourId(tour.id);
              const debugSuffix = debug ? '?debug=true' : '';
              router.push(`/tour/${tour.id}/round-init${debugSuffix}`);
            };
            const handleOpenTourEdit = () => {
              setCurrentTourId(tour.id);
              const debugSuffix = debug ? '?debug=true' : '';
              router.push(`/tour/${tour.id}/edit${debugSuffix}`);
            };
            const roundsState = roundsByTourId[tour.id];
            const rounds = roundsState?.rounds ?? [];
            const activeRounds = rounds.filter((round) => {
              const status = (round.status ?? '').toLowerCase();
              return status !== 'cancelled' && status !== 'finished';
            });
            const handleOpenRoundSettings = () => {
              const normalizedRounds = [...activeRounds].sort((a, b) => {
                const aTime = Date.parse(a.statusUpdatedAt ?? a.createdAt ?? '') || 0;
                const bTime = Date.parse(b.statusUpdatedAt ?? b.createdAt ?? '') || 0;
                return bTime - aTime;
              });
              const targetRound =
                normalizedRounds.find((round) => (round.status ?? '').toLowerCase() === 'draft') ??
                normalizedRounds.find((round) => {
                  const status = (round.status ?? '').toLowerCase();
                  return status === 'active' || status === 'playing';
                }) ??
                normalizedRounds[0];
              if (!targetRound) {
                setOpenTourMenuId(null);
                return;
              }
              setCurrentTourId(tour.id);
              setCurrentRoundId(targetRound.id);
              const queryParts: string[] = ['mode=update'];
              if (debug) {
                queryParts.push('debug=true');
              }
              router.push(`/round/${targetRound.id}/roundstart?${queryParts.join('&')}`);
              setOpenTourMenuId(null);
            };
            return (
              <View key={tour.id} style={[styles.card, menuOpen && styles.cardMenuOpen]}>
                <View style={styles.cardContent}>
                  <View style={styles.cardHeaderRow}>
                    <View style={styles.tourMenuAnchor}>
                      <Pressable
                        style={styles.tourMenuButton}
                        onPress={() => setOpenTourMenuId(menuOpen ? null : tour.id)}
                        accessibilityLabel={t('tournaments.menuLabel')}
                      >
                        <Text style={styles.tourMenuIcon}>⋯</Text>
                      </Pressable>
                      {menuOpen ? (
                        <View style={styles.tourMenu}>
                          <Pressable style={styles.tourMenuItem} onPress={handleOpenRoundSettings}>
                            <Text style={styles.tourMenuText}>{t('tournaments.roundSettings')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => void handleOpenShareModal(tour)}>
                            <Text style={styles.tourMenuText}>{t('tournaments.shareCta')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'archived')}>
                            <Text style={styles.tourMenuText}>{t('tournaments.statusArchive')}</Text>
                          </Pressable>
                          <Pressable
                            style={styles.tourMenuItem}
                            onPress={() => {
                              console.info('[tournaments] sleep selected', { tourId: tour.id });
                              logToBff('tournaments.status.selected', {
                                tourId: tour.id,
                                status: 'sleep',
                                requestId: generateId(),
                              });
                              setOpenTourMenuId(null);
                            }}
                          >
                            <Text style={styles.tourMenuText}>{t('tournaments.statusSleep')}</Text>
                          </Pressable>
                          <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'cancelled')}>
                            <Text style={styles.tourMenuText}>{t('tournaments.statusCancel')}</Text>
                          </Pressable>
                        </View>
                      ) : null}
                    </View>
                    <Pressable style={styles.cardRow} onPress={handleOpenTourEdit} accessibilityLabel={t('tournaments.editCta')}>
                      <Image source={imageSource} style={styles.tourImage} resizeMode="cover" />
                      <View style={styles.cardText}>
                        <View style={styles.cardTitleRow}>
                          <Text style={styles.cardTitle}>{tourTitle}</Text>
                          {statusLabel ? <Text style={styles.cardStatus}>{statusLabel}</Text> : null}
                        </View>
                        {playerLine ? <Text style={styles.meta}>{playerLine}</Text> : null}
                        {debug ? <Text style={styles.meta}>ID: {tour.id}</Text> : null}
                        <View style={styles.tourActions}>
                          <Pressable
                            style={styles.editButton}
                            onPress={handleOpenTourEdit}
                            accessibilityLabel={t('tournaments.editCta')}
                          >
                            <Text style={styles.editButtonText}>{t('tournaments.editCta')}</Text>
                          </Pressable>
                        </View>
                      </View>
                    </Pressable>
                  </View>
                  <View style={styles.roundsColumn}>
                    <View style={styles.roundsHeaderRow}>
                      <Text style={styles.roundsTitle}>
                        {t('tournaments.roundsTitle')}
                        {roundsState ? ` (${activeRounds.length})` : ''}
                      </Text>
                    </View>
                    {roundsState?.loading ? <Text style={styles.roundsLoading}>{t('tournaments.roundsLoading')}</Text> : null}
                    {roundsState?.error ? <Text style={styles.roundsError}>{roundsState.error}</Text> : null}
                    {!roundsState?.loading && !roundsState?.error && activeRounds.length === 0 ? (
                      <Text style={styles.roundsEmpty}>{t('tournaments.roundsEmpty')}</Text>
                    ) : null}
                    <View style={styles.roundsGrid}>
                      {activeRounds.map((round) => {
                        const roundName = round.name ?? round.courseName ?? t('tournaments.roundFallbackName');
                        const roundStatusLabel = formatRoundStatus(round);
                        const normalizedRoundStatus = (round.status ?? '').toLowerCase();
                        const isFinished = normalizedRoundStatus === 'finished';
                        const isDraft = normalizedRoundStatus === 'draft';
                        const roundActionLabel = isFinished
                          ? t('tournaments.roundResults')
                          : isDraft
                            ? t('tournaments.roundStart')
                            : t('tournaments.roundOpen');
                        const debugSuffix = debug ? '?debug=true' : '';
                        const roundPath = isFinished
                          ? `/round/${round.id}/summary${debugSuffix}`
                          : normalizedRoundStatus === 'draft'
                            ? `/round/${round.id}/roundstart${debugSuffix}`
                            : `/round/${round.id}/hole/1/compact${debugSuffix}`;
                        const roundGamesState = roundGamesByRoundId[round.id];
                        const roundGameNames = (roundGamesState?.gameIds ?? [])
                          .map((id) => gameNameById.get(id) ?? t('tournaments.gameFallbackName'))
                          .filter(Boolean);
                        return (
                          <Pressable
                            key={round.id}
                            style={styles.roundRow}
                            onPress={() => {
                              setCurrentTourId(tour.id);
                              setCurrentRoundId(round.id);
                              router.push(roundPath);
                            }}
                            accessibilityLabel={roundName}
                          >
                            <View style={styles.roundCard}>
                              <View style={styles.roundHeaderRow}>
                                <View style={styles.roundHeader}>
                                  <Text style={styles.roundName}>{roundName}</Text>
                                  {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                                </View>
                                <GoffyButton
                                  label={roundActionLabel}
                                  onPress={() => {
                                    setCurrentTourId(tour.id);
                                    setCurrentRoundId(round.id);
                                    router.push(roundPath);
                                  }}
                                  size="compact"
                                  tone="dark"
                                  oldButtonStyle={styles.roundActionButton}
                                  oldDisabledStyle={styles.primaryButtonDisabled}
                                  style={styles.roundActionButtonWrap}
                                  textStyle={styles.roundActionButtonText}
                                />
                              </View>
                              <View style={styles.roundGamesSection}>
                                {roundGamesState?.loading ? (
                                  <Text style={styles.roundGamesMeta}>{t('tournaments.gamesLoading')}</Text>
                                ) : null}
                                {roundGamesState?.error ? (
                                  <Text style={styles.roundGamesError}>{roundGamesState.error}</Text>
                                ) : null}
                                {!roundGamesState?.loading && !roundGamesState?.error ? (
                                  <Text style={styles.roundGamesMeta}>
                                    {t('tournaments.gamesCount', { count: roundGameNames.length })}
                                  </Text>
                                ) : null}
                                <View style={styles.roundGamesList} />
                              </View>
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                  <GoffyButton
                    label={t('tournaments.newRoundCta')}
                    onPress={handleNewRound}
                    tone="accent"
                    oldButtonStyle={styles.newRoundButton}
                    oldTextStyle={styles.newRoundButtonText}
                    oldDisabledStyle={styles.primaryButtonDisabled}
                    style={styles.newRoundButtonWide}
                  />
                </View>
              </View>
            );
          }) : null}
          {isMyToursPage && !visibleTours.length && !loading && !error ? <Text style={styles.subtitle}>{t('tournaments.noTours')}</Text> : null}
        </ScrollView>
        <View style={styles.sidePanel}>
          <Image source={trophyImage} style={styles.heroImage} resizeMode="contain" />
          {isCreateTourPage ? (
            <>
              <TextInput
                value={newTourName}
                onChangeText={setNewTourName}
                placeholder={t('tournaments.namePlaceholder')}
                placeholderTextColor={appColors.accent.primary}
                style={styles.tourInput}
                editable={!playerProvisioningBlocked}
              />
              <View style={styles.membersCard}>
                <Text style={styles.membersTitle}>{t('tournaments.membersTitle')}</Text>
                <Text style={styles.meta}>
                  {t('tournaments.membersCount', { selected: effectiveSelectedPlayerIds.length, max: MAX_PLAYERS })}
                </Text>
                {loadingPlatformPlayers ? <Text style={styles.meta}>{t('tournaments.membersLoading')}</Text> : null}
                <View style={styles.membersGrid}>
                  {availablePlayers.map((player) => {
                    const selected = selectedPlayerIds.includes(player.id);
                    const creatorLocked = creatorPlayerId === player.id;
                    return (
                      <Pressable
                        key={player.id}
                        style={[styles.memberChip, selected && styles.memberChipSelected, creatorLocked && styles.memberChipLocked]}
                        onPress={() => togglePlayerSelection(player.id)}
                        disabled={playerProvisioningBlocked}
                      >
                        <Text style={[styles.memberChipText, selected && styles.memberChipTextSelected, creatorLocked && styles.memberChipTextLocked]}>
                          {player.name}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
                <View style={styles.temporaryRow}>
                  <TextInput
                    value={temporaryPlayerName}
                    onChangeText={setTemporaryPlayerName}
                    placeholder={t('players.temporaryNamePlaceholder')}
                    placeholderTextColor={appColors.accent.primary}
                    style={styles.temporaryInput}
                    editable={!playerProvisioningBlocked}
                  />
                  <GoffyButton
                    label={t('players.temporaryCreateAction')}
                    onPress={handleCreateTemporaryPlayer}
                    disabled={tourStarting || pendingTemporaryCreates > 0 || playerProvisioningBlocked}
                    size="compact"
                    tone="dark"
                    oldButtonStyle={styles.secondaryButton}
                    oldTextStyle={styles.secondaryButtonText}
                    oldDisabledStyle={styles.primaryButtonDisabled}
                    style={styles.secondaryButtonImageMode}
                    textStyle={styles.secondaryButtonImageText}
                  />
                </View>
                {pendingTemporaryCreates > 0 ? (
                  <Text style={styles.meta}>
                    {t('players.temporaryCreatePendingCount', { count: pendingTemporaryCreates })}
                  </Text>
                ) : null}
              </View>
              <View ref={createTourButtonAnchorRef}>
                <GoffyButton
                  label={tourStarting ? t('tournaments.creating') : t('tournaments.newRoundCta')}
                  onPress={handleNewTour}
                  disabled={tourStarting || playerProvisioningBlocked}
                  size="compact"
                  tone="accent"
                  oldButtonStyle={styles.primaryButton}
                  oldTextStyle={styles.primaryText}
                  oldDisabledStyle={styles.primaryButtonDisabled}
                  style={styles.primaryButtonImageMode}
                />
              </View>
            </>
          ) : (
            <View ref={createTourButtonAnchorRef}>
              <GoffyButton
                label={t('tournaments.createCta')}
                onPress={() => router.push(debug ? '/tournaments?debug=true' : '/tournaments')}
                size="compact"
                tone="accent"
                oldButtonStyle={styles.primaryButton}
                oldTextStyle={styles.primaryText}
                style={styles.primaryButtonImageMode}
              />
            </View>
          )}
        </View>
      </View>
      <CoachMarkCallout
        visible={showCreateTourCoachMark}
        targetRef={createTourButtonAnchorRef}
        title={t('coachMarks.tournamentsCreateTitle')}
        description={t('coachMarks.tournamentsCreateBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={dismissCreateTourCoachMark}
      />
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <View style={styles.shareOverlay}>
          <View style={styles.shareCard}>
            <Text style={styles.shareTitle}>
              {t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') })}
            </Text>
            <Text style={styles.shareSubtitle}>{t('tournaments.shareSubtitle')}</Text>
            {shareModalLoading ? (
              <ActivityIndicator size="small" color={appColors.accent.primary} />
            ) : shareModalLink ? (
              <View style={styles.shareQrWrapper}>
                <QRCode value={shareModalLink} size={240} color="#000000" backgroundColor="#ffffff" />
              </View>
            ) : null}
            {shareModalLink ? (
              <Text selectable style={styles.shareLinkText}>
                {shareModalLink}
              </Text>
            ) : null}
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="dark"
                oldButtonStyle={styles.secondaryButton}
                oldTextStyle={styles.secondaryButtonText}
                oldDisabledStyle={styles.primaryButtonDisabled}
                style={styles.shareButtonImageMode}
                textStyle={styles.secondaryButtonImageText}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setShareModalOpen(false)}
                tone="dark"
                oldButtonStyle={styles.primaryButton}
                oldTextStyle={styles.primaryText}
                style={styles.shareButtonImageMode}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: appColors.background.app,
    padding: 28,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 20,
  },
  headerText: {
    gap: 6,
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  meta: {
    color: appColors.accent.primary,
  },
  provisioningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  provisioningRetryRow: {
    gap: 8,
  },
  retryButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  retryButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
  },
  error: {
    color: '#ff9fa6',
    fontWeight: '700',
  },
  body: {
    flex: 1,
    flexDirection: 'row',
    gap: 24,
  },
  list: {
    flex: 1,
  },
  listContent: {
    gap: 16,
    paddingBottom: 40,
  },
  sidePanel: {
    width: 320,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 12,
    alignSelf: 'flex-start',
  },
  heroImage: {
    width: 120,
    height: 120,
    alignSelf: 'center',
  },
  subtitle: {
    color: appColors.text.secondary,
  },
  smallNote: {
    color: appColors.text.secondary,
  },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
    zIndex: 1,
  },
  cardMenuOpen: {
    borderColor: '#A1D493',
    zIndex: 10,
    elevation: 10,
  },
  cardContent: {
    width: '100%',
    gap: 12,
  },
  cardHeaderRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  cardColumns: {
    flexDirection: 'column',
    gap: 4,
    alignItems: 'stretch',
  },
  cardRow: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    alignItems: 'flex-start',
  },
  cardText: {
    flex: 1,
    gap: 6,
  },
  tourImage: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourInfo: {
    alignSelf: 'stretch',
    width: '100%',
  },
  tourMenuAnchor: {
    position: 'relative',
    marginRight: 12,
    zIndex: 20,
  },
  tourMenuButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tourMenuIcon: {
    color: appColors.text.secondary,
    fontSize: 18,
    fontWeight: '600',
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  roundsColumn: {
    width: '100%',
    gap: 10,
  },
  roundsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  roundsTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  roundsLoading: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundsEmpty: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundsError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundRow: {
    width: '100%',
    alignSelf: 'stretch',
  },
  roundCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
  },
  roundHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  roundHeader: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  roundName: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '700',
  },
  roundMeta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundActionButtonWrap: {
    alignSelf: 'center',
    marginLeft: 'auto',
  },
  roundActionButton: {
    paddingHorizontal: 10,
    height: 36,
  },
  roundActionButtonText: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '700',
  },
  hiddenRoundActions: {
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-end',
    marginLeft: 'auto',
  },
  hiddenRoundActionButtonWrap: {
    width: 70,
  },
  hiddenRoundCancelButton: {
    paddingHorizontal: 10,
    height: 36,
    borderColor: '#8c2f2f',
  },
  hiddenRoundCancelButtonText: {
    color: '#ffb3b3',
    fontSize: 14,
    fontWeight: '700',
  },
  roundGamesSection: {
    gap: 6,
  },
  roundGamesTitle: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  roundGamesMeta: {
    color: '#A1D493',
    fontSize: 12,
  },
  roundGamesError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundGamesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  roundGameTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(52,211,153,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
  },
  roundGameText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  roundsGrid: {
    gap: 8,
  },
  cardStatus: {
    fontSize: 12,
    fontWeight: '700',
    color: '#A1D493',
    backgroundColor: 'rgba(45,98,53,0.24)',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 2,
  },
  newRoundButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  newRoundButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  newRoundButtonWide: {
    alignSelf: 'stretch',
    width: '100%',
  },
  tourActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 6,
  },
  editButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.4)',
    backgroundColor: 'rgba(47,138,78,0.12)',
  },
  editButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  tourMenu: {
    position: 'absolute',
    left: 0,
    top: 44,
    backgroundColor: appColors.background.panel,
    borderRadius: 10,
    padding: 10,
    gap: 6,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.32)',
    minWidth: 140,
    zIndex: 25,
  },
  tourMenuItem: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tourMenuText: {
    color: appColors.text.secondary,
    fontWeight: '600',
  },
  debugUser: {
    color: '#A1D493',
    fontSize: 12,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panelAlt,
  },
  toggleButtonActive: {
    backgroundColor: 'rgba(45,98,53,0.35)',
  },
  toggleButtonText: {
    color: appColors.text.primary,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  toggleIndicator: {
    width: 30,
    height: 18,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(124,171,134,0.65)',
    padding: 2,
    justifyContent: 'center',
  },
  toggleDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    backgroundColor: '#8fc49b',
  },
  toggleDotActive: {
    alignSelf: 'flex-end',
    backgroundColor: '#A1D493',
  },
  tourInput: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.app,
    color: appColors.text.heading,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  membersCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    padding: 10,
    gap: 8,
  },
  membersTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  memberChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  memberChipSelected: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(52,211,153,0.18)',
  },
  memberChipLocked: {
    borderColor: '#8ecf9a',
    backgroundColor: 'rgba(42,119,66,0.18)',
  },
  memberChipText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  memberChipTextSelected: {
    color: '#323835',
  },
  memberChipTextLocked: {
    color: '#2d6b66',
  },
  temporaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  temporaryInput: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 220,
    minWidth: 0,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  secondaryButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    paddingVertical: 8,
    maxWidth: '100%',
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  secondaryButtonImageMode: {
    width: 124,
    height: 34,
  },
  secondaryButtonImageText: {
    fontSize: 14,
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  shareCard: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.elevated,
    padding: 20,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.heading,
    fontSize: 21,
    fontWeight: '800',
  },
  shareSubtitle: {
    color: appColors.text.secondary,
    fontSize: 13,
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareLinkText: {
    color: appColors.text.primary,
    fontSize: 12,
  },
  shareActions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-end',
  },
  primaryButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonImageMode: {
    width: 124,
    height: 34,
  },
  shareButtonImageMode: {
    width: 180,
    height: 56,
  },
  primaryText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 16,
  },
});

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
